/**
 * Configuração do i18next.
 *
 * Dois pontos que valem explicação:
 *
 * **Locale habilitado ≠ locale existente.** Os três catálogos estão no
 * repositório desde o Marco 0, mas só entra no seletor o que `VITE_ENABLED_LOCALES`
 * autoriza — e um idioma só entra ali depois da revisão manual de tradução.
 * Publicar `en-US` com texto provisório é pior do que não oferecer o idioma:
 * quem escolhe inglês passa a confiar no que lê.
 *
 * **Carregamento sob demanda.** Quem usa em português não baixa espanhol.
 * Com três idiomas o ganho é modesto; com os catálogos completos do Marco 5,
 * não é.
 */

import i18next, { type i18n as I18nInstance } from 'i18next';
import { initReactI18next } from 'react-i18next';

export const SUPPORTED_LOCALES = ['pt-BR', 'en-US', 'es-419'] as const;
export type Locale = (typeof SUPPORTED_LOCALES)[number];

export const DEFAULT_LOCALE: Locale = (import.meta.env.VITE_DEFAULT_LOCALE ?? 'pt-BR') as Locale;

export const ENABLED_LOCALES: Locale[] = (import.meta.env.VITE_ENABLED_LOCALES ?? 'pt-BR')
  .split(',')
  .map((value: string) => value.trim())
  .filter((value: string): value is Locale =>
    (SUPPORTED_LOCALES as readonly string[]).includes(value),
  );

export const NAMESPACES = [
  'common',
  'auth',
  'errors',
  'onboarding',
  'profiles',
  'relationships',
  'curation',
  'templates',
  'plans',
] as const;
const STORAGE_KEY = 'movhuman.locale';

const catalogs = import.meta.glob<{ default: Record<string, unknown> }>('./locales/*/[!_]*.json');

async function loadLocale(locale: Locale): Promise<void> {
  await Promise.all(
    NAMESPACES.map(async (namespace) => {
      if (i18next.hasResourceBundle(locale, namespace)) return;
      const loader = catalogs[`./locales/${locale}/${namespace}.json`];
      if (!loader) return;
      const module = await loader();
      i18next.addResourceBundle(locale, namespace, module.default, true, true);
    }),
  );
}

/** `pt-BR` → `pt`. Sempre presente: `split` nunca devolve lista vazia. */
function baseLanguage(tag: string): string {
  return (tag.split('-')[0] ?? tag).toLowerCase();
}

/**
 * Ordem de decisão: escolha explícita → idioma do navegador → padrão.
 *
 * O perfil do usuário entra depois, quando a sessão carrega — não aqui, porque
 * a primeira tela precisa de idioma antes de existir sessão.
 */
export function detectLocale(): Locale {
  const stored = localStorage.getItem(STORAGE_KEY) as Locale | null;
  if (stored && ENABLED_LOCALES.includes(stored)) return stored;

  for (const candidate of navigator.languages ?? [navigator.language]) {
    const exact = ENABLED_LOCALES.find((l) => l.toLowerCase() === candidate.toLowerCase());
    if (exact) return exact;
    // `es-AR` cai em `es-419`; `pt-PT` cai em `pt-BR`. Melhor um português
    // do Brasil do que uma tela em inglês para quem pediu português.
    const base = baseLanguage(candidate);
    const partial = ENABLED_LOCALES.find((l) => baseLanguage(l) === base);
    if (partial) return partial;
  }

  return ENABLED_LOCALES.includes(DEFAULT_LOCALE)
    ? DEFAULT_LOCALE
    : (ENABLED_LOCALES[0] ?? 'pt-BR');
}

export async function changeLocale(locale: Locale): Promise<void> {
  if (!ENABLED_LOCALES.includes(locale)) return;
  await loadLocale(locale);
  await i18next.changeLanguage(locale);
  localStorage.setItem(STORAGE_KEY, locale);
  document.documentElement.lang = locale;
}

export async function setupI18n(): Promise<I18nInstance> {
  const locale = detectLocale();

  await i18next.use(initReactI18next).init({
    lng: locale,
    fallbackLng: DEFAULT_LOCALE,
    supportedLngs: ENABLED_LOCALES,
    ns: [...NAMESPACES],
    defaultNS: 'common',
    resources: {},
    interpolation: {
      // O React já escapa tudo o que renderiza. Escapar de novo transformaria
      // "Você" em "Voc&ecirc;" na tela.
      escapeValue: false,
    },
    returnEmptyString: false,
    // Chave sem tradução vira erro de build no `npm run i18n:check`. Se ainda
    // assim escapar uma, é melhor mostrar o texto do idioma de origem do que a
    // chave crua — "common:action.start" na tela não ajuda ninguém.
    parseMissingKeyHandler: (key) => key.split('.').pop() ?? key,
  });

  await loadLocale(locale);
  if (locale !== DEFAULT_LOCALE) await loadLocale(DEFAULT_LOCALE);
  document.documentElement.lang = locale;

  return i18next;
}

export default i18next;
